import { useEffect, useMemo, useState, type HTMLAttributes } from 'react'

import { log, setupLiveKitRoom } from '@livekit/components-core'
import {
  Room,
  MediaDeviceFailure,
  RoomEvent,
  ConnectionState,
  RemoteParticipant,
  DataPacket_Kind,
} from 'livekit-client'

import axios, { AxiosResponse } from 'axios'

import type { LiveKitRoomProps } from '@/src/components'

import { mergeProps } from '@/src/mergeProps'

import { handlePublishData } from '@/src/helpers/handlePublishData'

import { IRemoveParticipantBody } from '@/src/types/participant.type'

import { useAppSelector } from '@/src/store/hooks'
import { selectRoomState } from '@/src/store/slices/room'
import { useLocalMicrophonePermissions } from '@/src/hooks/room/useLocalMicrophonePermissions'

const defaultRoomProps: Partial<LiveKitRoomProps> = {
  connect: true,
  audio: false,
  video: false,
}

/**
 * The `useLiveKitRoom` hook is used to implement the `LiveKitRoom` or your custom implementation of it.
 * It returns a `Room` instance and HTML props that should be applied to the root element of the component.
 *
 * @example
 * ```tsx
 * const { room, htmlProps } = useLiveKitRoom();
 * return <div {...htmlProps}>...</div>;
 * ```
 * @public
 */
export function useLiveKitRoom<T extends HTMLElement>(
  props: LiveKitRoomProps,
): {
  room: Room | undefined
  htmlProps: HTMLAttributes<T>
} {
  const {
    token,
    serverUrl,
    options,
    room: passedRoom,
    connectOptions,
    connect,
    audio,
    enforceRoomMicrophoneBan,
    video,
    screen,
    onConnected,
    onDisconnected,
    onError,
    onMediaDeviceFailure,
    onEncryptionError,
    simulateParticipants,
    ...rest
  } = { ...defaultRoomProps, ...props }
  if (options && passedRoom) {
    log.warn(
      'when using a manually created room, the options object will be ignored. set the desired options directly when creating the room instead.',
    )
  }
  const [room, setRoom] = useState<Room | null>(null)
  const { livekit_url } = useAppSelector(selectRoomState)
  const canEnableMicrophone = useLocalMicrophonePermissions(room, {
    enforceRoomMicrophoneBan, token, serverUrl,
  })

  useEffect(() => {
    setRoom(passedRoom ?? new Room(options))
  }, [passedRoom, options])

  const htmlProps = useMemo(() => {
    const { className } = setupLiveKitRoom()
    return mergeProps(rest, { className }) as any
  }, [rest])

  useEffect(() => {
    if (!room) return

    const onSignalConnected = () => {
      const localP = room.localParticipant
      log.debug('trying to publish local tracks')
      Promise.all([
        localP.setMicrophoneEnabled(
          !!audio && canEnableMicrophone(),
          typeof audio !== 'boolean' ? audio : undefined,
        ),
        localP.setScreenShareEnabled(!!screen, typeof screen !== 'boolean' ? screen : undefined),
        localP.setCameraEnabled(!!video, typeof video !== 'boolean' ? video : undefined),
      ]).catch((e) => {
        log.warn(e)
        onError?.(e as Error)
      })
    }

    const handleMediaDeviceError = (e: Error) => {
      const mediaDeviceFailure = MediaDeviceFailure.getFailure(e)
      onMediaDeviceFailure?.(mediaDeviceFailure)
    }

    const handleEncryptionError = (e: Error) => {
      onEncryptionError?.(e)
    }

    room
      .on(RoomEvent.SignalConnected, onSignalConnected)
      .on(RoomEvent.MediaDevicesError, handleMediaDeviceError)
      .on(RoomEvent.EncryptionError, handleEncryptionError)

    return () => {
      room
        .off(RoomEvent.SignalConnected, onSignalConnected)
        .off(RoomEvent.MediaDevicesError, handleMediaDeviceError)
        .off(RoomEvent.EncryptionError, handleEncryptionError)
    }
  }, [room, audio, video, screen, canEnableMicrophone, onError, onEncryptionError, onMediaDeviceFailure])

  useEffect(() => {
    if (!room) return

    if (simulateParticipants) {
      room.simulateParticipants({
        participants: {
          count: simulateParticipants,
        },
        publish: {
          audio: true,
          useRealTracks: true,
        },
      })
      return
    }

    if (!token) {
      log.debug('no token yet')
      return
    }

    if (!serverUrl) {
      log.warn('no livekit url provided')
      onError?.(Error('no livekit url provided'))

      return
    }

    if (connect) {
      // Повторный connect в Connected не подменяет JWT и только шумит в логах.
      // Elevated-токен после впуска хранится в токене пропа, retry зайдёт уже с ним.
      if (
        room.state === ConnectionState.Connected ||
        room.state === ConnectionState.Connecting ||
        room.state === ConnectionState.Reconnecting
      ) {
        return
      }

      log.debug('connecting')

      const msg = `Connection is starting with options: serverUrl - ${serverUrl}, token - ${token}, connectOptions - ${JSON.stringify(
        connectOptions,
      )}`

      console.log(msg)
      log.debug(msg)

      room.connect(serverUrl, token, connectOptions).catch((e) => {
        log.error(`${JSON.stringify(e)}`)
        onError?.(e as Error)
      })
    } else {
      log.error(`disconnecting because connect is false. Server - ${serverUrl}, token - ${token}`)
      room.disconnect()
    }
  }, [connect, token, connectOptions, room, onError, serverUrl, simulateParticipants])

  useEffect(() => {
    if (!room) return

    const connectionStateChangeListener = (state: ConnectionState) => {
      switch (state) {
        case ConnectionState.Disconnected:
          if (onDisconnected) onDisconnected()
          break
        case ConnectionState.Connected:
          if (onConnected) onConnected()
          break

        default:
          break
      }
    }

    room.on(RoomEvent.ConnectionStateChanged, connectionStateChangeListener)

    return () => {
      room.off(RoomEvent.ConnectionStateChanged, connectionStateChangeListener)
    }
  }, [token, onConnected, onDisconnected, room])

  useEffect(() => {
    if (!room) {
      return () => {
        log.info("Don't have any room context")
      }
    }
  }, [room])

  useEffect(() => {
    if (!room) {
      return () => {
        log.info("Don't have any room context")
      }
    }

    const handleRemoveParticipant = async (identity: string) => {
      if (!livekit_url) return

      axios
        .post<void, AxiosResponse<void>, IRemoveParticipantBody>(
          `${window.origin}/api/remove_participant?livekit_url=${livekit_url}`,
          {
            room: room?.name,
            identity: identity,
          },
        )
        ?.catch((error) => {
          console.error(`Error with removing disconnected remote participant: ${error}`)
        })
    }

    const handleReloadPage = async (event: BeforeUnloadEvent) => {
      if (!livekit_url) return

      const firstParticipant = room?.remoteParticipants?.keys()?.next()?.value

      handleRemoveParticipant(room?.localParticipant?.identity)

      if (room) {
        handlePublishData(
          room,
          { participantIdentity: room?.localParticipant?.identity },
          {
            reliable: true,
            topic: 'remove-participant',
            destinationIdentities: [firstParticipant],
          },
        )?.catch((error) => {
          console.error(`Error with publish data: `, error)
        })
      }
    }

    const handleDataReceived = async (
      payload: Uint8Array,
      participant?: RemoteParticipant,
      kind?: DataPacket_Kind,
      topic?: string,
    ) => {
      if (topic?.includes('remove-participant')) {
        try {
          const decoder = new TextDecoder('utf-8')
          const obj = JSON.parse(decoder.decode(payload) ?? '{}')

          handleRemoveParticipant(obj?.participantIdentity)
        } catch (e) {
          console.error('Error with decoding message: \n', e)
        }
      }
    }

    window.addEventListener('beforeunload', handleReloadPage)
    room?.on(RoomEvent.DataReceived, handleDataReceived)

    return () => {
      log.info('disconnecting on onmount')
      window.removeEventListener('beforeunload', handleReloadPage)
      room?.off(RoomEvent.DataReceived, handleDataReceived)
    }
  }, [room, livekit_url, room?.remoteParticipants?.size])

  return { room, htmlProps }
}
